import { computed, effect, inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { firstValueFrom, Observable, of, throwError } from 'rxjs';
import { catchError, filter, map, take, tap, timeout } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  AppleSigninRequest,
  CheckCompanyCodeExistsResponse,
  CheckSignupUserEmailExistsRequest,
  CheckSignupUserEmailExistsResponse,
  CompleteProfileFormData,
  FacebookSigninRequest,
  ForgotPasswordRequest,
  GoogleSigninRequest,
  LinkedinSigninRequest,
  LoginFormData,
  LoginRequest,
  LoginUserListItem,
  MicrosoftSigninRequest,
  ProfileLoginRequest,
  Permission,
  PlanSelection,
  ResetPasswordRequest,
  ResetPasswordWithTokenRequest,
  SendOtpRequest,
  SIGNUP_LOCATION_ID,
  SignupFormData,
  SignupRequest,
  SocialLoginPayload,
  SubscriptionAccess,
  UpdateOnboardingDetailsRequest,
  UserRegisterType,
  VerifyForgotPasswordOtpRequest,
  VerifyOtpRequest,
} from '../../features/auth/models/auth.models';
import { PaymentPlanContext, resolvePlanContext } from '../../features/auth/models/payment.models';
import { SubscriptionPlanService } from '../../features/auth/service/subscription-plan.service';
import { AuthStore } from '../../features/auth/store/auth.store';
import { AUTH_TOKEN_STORAGE_KEY, StorageService } from './storage.service';
import { LocalStorageService } from './local-storage.service';
import { EncryptionService } from './encryption.service';
import { decodeJwt, isTokenExpired } from '../utils/jwt.utils';
import { resolveErrorMessage } from '../utils/error.util';
import { ApiService } from '../api/api.service';
import { API_ENDPOINTS } from '../api/endpoints';
import { ResponseCode } from '../api/response-codes';
import { ToastService } from '../../shared/services/toast.service';
import { UserInitStore } from '../store/user-init/user-init.store';
import { StoreResetService } from './store-reset.service';
import { WhatsAppConnectionStore } from '../../features/settings/components/communications/whatsapp/store/whatsapp-connection.store';
import { LocationStore } from '../store/location.store';

/** Per-tab identity, kept in sessionStorage so a page reload reuses the same id. */
export const TAB_ID_KEY = 'pp_tab_id';
/** Shared registry of open tabs (tabId → last-seen timestamp), kept in localStorage. */
export const ACTIVE_TABS_KEY = 'pp_active_tabs';
/** How often an open tab refreshes its own "still open" timestamp. */
const TAB_HEARTBEAT_INTERVAL_MS = 5000;
/** A tab not refreshed within this window is treated as closed/crashed. */
export const TAB_STALE_THRESHOLD_MS = 15000;
/** CheckCompanyCodeExists responds with Success:false + this code when the clinic name is taken. */
const CLINIC_NAME_CONFLICT_RESPONSE_CODE = 443;

type TabRegistry = Record<string, number>;


@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly api = inject(ApiService);
  private readonly store = inject(AuthStore);
  private readonly storage = inject(StorageService);
  private readonly ls = inject(LocalStorageService);
  private readonly encryption = inject(EncryptionService);
  private readonly router = inject(Router);
  private readonly subscriptionPlan = inject(SubscriptionPlanService);
  private readonly toast = inject(ToastService);
  private readonly userInit = inject(UserInitStore);
  private readonly storeReset = inject(StoreResetService);
  private readonly waConnection = inject(WhatsAppConnectionStore);
  private readonly locationStore = inject(LocationStore);

  private ipAddress: string | null = null;
  private ipLocation: string | null = null;
  private deviceType = 'Web';

  /**
   * True only when this tab's sessionStorage had no tab id yet — i.e. this is a
   * brand-new browsing context (new tab, or the browser was actually closed and
   * reopened), not a reload of a tab that was already open. `pagehide` fires on
   * an ordinary refresh too, so sibling-tab presence alone can't tell a refresh
   * apart from a real close; this flag is what makes that distinction (set inside
   * resolveTabId(), which runs via the tabId field initializer right below).
   */
  private isNewTab = false;

  /**
   * Identifies this browser tab across page reloads (sessionStorage survives a
   * reload but not a tab close), so this tab can recognise and skip its own
   * entry when checking the shared active-tab registry below.
   */
  private readonly tabId = this.resolveTabId();
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  /** Shared network/device fields every login request DTO carries. */
  private get networkContext(): { ipLocation: string | null; ipAddress: string | null; deviceType: string } {
    return { ipLocation: this.ipLocation, ipAddress: this.ipAddress, deviceType: this.deviceType };
  }

  // ── Public read-only signals ───────────────────────────────────────────────
  readonly isLoggedIn = this.store.isLoggedIn;
  /** True once api/Role/GetByUserId has resolved (success or failure) for the current session. */
  readonly permissionsLoaded = this.store.permissionsLoaded;
  /** True when that fetch failed because the request never reached the server (no internet),
   *  rather than a real permission denial — see the doc comment on `AuthState.permissionsLoadOffline`. */
  readonly permissionsLoadOffline = this.store.permissionsLoadOffline;
  readonly isLoading = this.store.isLoading;
  readonly isTrial = this.store.isTrial;
  readonly error = this.store.error;
  readonly redirectMenu = this.store.redirectMenu;
  readonly requiresTwoFactor = this.store.requiresTwoFactor;
  readonly twoFactorEmail = this.store.twoFactorEmail;
  readonly requiresSignupOtp = this.store.requiresSignupOtp;
  readonly requiresProfileSelection = this.store.requiresProfileSelection;
  readonly profileUsers = this.store.profileUsers;
  readonly profileLoginPassword = this.store.profileLoginPassword;
  readonly pendingSignupEmail = this.store.pendingSignupEmail;
  readonly pendingSignupPhone = this.store.pendingSignupPhone;
  readonly needsPlanSelection = this.store.needsPlanSelection;
  readonly needsProfileCompletion = this.store.needsProfileCompletion;
  readonly selectedPlanId = this.store.selectedPlanId;
  readonly selectedBillingCycle = this.store.selectedBillingCycle;
  readonly selectedCurrencyCode = this.store.selectedCurrencyCode;
  readonly countries = this.store.countries;
  readonly completingProfile = this.store.completingProfile;
  readonly completeProfileError = this.store.completeProfileError;
  readonly onboardingProvisioning = this.store.onboardingProvisioning;
  readonly onboardingJobDone = this.store.onboardingJobDone;
  readonly needsPayment = this.store.needsPayment;
  readonly paymentCompanyId = this.store.paymentCompanyId;
  readonly needsTermsAcceptance = this.store.needsTermsAcceptance;
  readonly termsSkipCount = this.store.termsSkipCount;
  /** `true` while any mandatory onboarding overlay (choose-plan, provisioning, complete-profile,
   *  terms) is covering the screen. Shared by `ShellComponent` (unmounts `<router-outlet>`),
   *  `HeaderComponent`, and `SidebarComponent` (defer their own init API calls) so nothing loads
   *  data against a backend the account isn't fully provisioned against yet — see each
   *  component's own usage for why. Single source of truth so the four gates it composes can't
   *  drift out of sync between call sites. */
  readonly onboardingBlocking = computed(() =>
    this.needsPlanSelection() || this.onboardingProvisioning() || this.needsProfileCompletion() || this.needsTermsAcceptance(),
  );
  readonly resetPasswordEmail = this.store.resetPasswordEmail;
  readonly resetPasswordOtpVerified = this.store.resetPasswordOtpVerified;
  /** Signed-in clinic's currency symbol; falls back to '₹' until `User/GetById` resolves or if it omits one. */
  readonly currencySymbol = computed(() => this.store.currencySymbol() ?? '₹');
  /** Signed-in company's subscription plan display name, shown as a badge next to the sidebar logo. */
  readonly planName = this.store.planName;

  readonly user = computed(() => ({
    name: this.store.userName() ?? '',
    initials: this.buildInitials(this.store.userName()),
    role: this.store.roles()[0] ?? '',
    email: this.store.email() ?? '',
    clinicName: this.store.companyName() ?? '',
  }));

  constructor() {
    this.handleAdminLoginFromUrl();
    this.restoreSession();
    void this.validateAdminLoginAsync();
    this.getUserInfo();
    this.initCrossTabSync();

    // A fresh registration sets the onboarding-blocking flags (needsPlanSelection etc.) on the
    // very same response withPermissionsRefresh() reacts to, so its own tap() below can't fire
    // loadMasterData() yet there — it defers instead. This is what actually fires it once the
    // choose-plan/provisioning/complete-profile/terms gate clears (login/restoreSession's own
    // immediate calls are unaffected: onboardingBlocking() is already false for them, so
    // loadMasterData()'s own masterDataStarted guard has already run by the time this fires).
    effect(() => {
      if (this.onboardingBlocking()) return;
      if (!this.isLoggedIn()) return;
      // Admin redirect: defer until validateAdminLoginAsync() sets the correct locationId.
      if (this.pendingAdminUserId) return;
      const userId = this.store.userId();
      if (userId) this.loadMasterData(userId);
    });
  }
  waitUntilPermissionsReady(): Promise<void> {
    if (!this.isLoggedIn() || this.permissionsLoaded()) return Promise.resolve();

    return Promise.race([
      firstValueFrom(
        toObservable(this.permissionsLoaded).pipe(filter(loaded => loaded), take(1)),
      ).then(() => void 0),
      new Promise<void>(resolve => setTimeout(resolve, 8000)),
    ]);
  }

  // ── Login ──────────────────────────────────────────────────────────────────
  // Each method here only builds the strongly-typed request payload and
  // delegates the HTTP call + state management to AuthStore.
  /** Guards `loadMasterData()` so it fires at most once per signed-in session, however many of
   *  withPermissionsRefresh()'s tap / the constructor's deferred effect / restoreSession() end up
   *  racing to call it — reset on logout so the next sign-in (possibly a different user) re-fetches. */
  private masterDataStarted = false;

  /** Permissions + the Master Store (ProfileData/GetById/GetClinicProfile) + the WhatsApp
   *  purchase-status check — the same "load once, right after the account is actually usable"
   *  bundle previously fired unconditionally from withPermissionsRefresh()/restoreSession(). Not
   *  needed by any onboarding overlay (choose-plan/provisioning/complete-profile/terms all render
   *  from data already on the login/registration response), so callers gate this on
   *  `!onboardingBlocking()` first — see withPermissionsRefresh() and the constructor effect. */
  private loadMasterData(userId: string): void {
    if (this.masterDataStarted) return;
    this.masterDataStarted = true;

    this.store.loadPermissions(userId);
    // Fire the Master Store's forkJoin (ProfileData + GetById) for login/registration —
    // internally guarded, so this never duplicates an already-loaded/in-flight fetch.
    this.userInit.initialize();
    // One-shot check of whether this company purchased the WhatsApp add-on — gates the
    // header status pill and the Account Overview/Plans "WhatsApp" tile (see
    // WhatsAppConnectionStore.purchased's doc comment).
    void this.waConnection.checkPurchaseStatus();
  }

  private withPermissionsRefresh(source$: Observable<void>): Observable<void> {
    return source$.pipe(
      tap(() => {
        const userId = this.store.userId();
        // A fresh registration's response also sets needsPlanSelection/needsProfileCompletion/etc.
        // synchronously before this tap runs — skip while that gate is up; the constructor's
        // effect above fires loadMasterData() once it clears instead of loading data (profile,
        // clinic profile, permissions) for an account that isn't fully provisioned yet.
        if (userId && !this.onboardingBlocking()) {
          this.loadMasterData(userId);
        }
      }),
    );
  }

  login(formData: LoginFormData): Observable<void> {
    const payload: LoginRequest = {
      Email: formData.email,
      Password: this.encryption.encrypt(formData.password),
      agree: null,
      Domain: environment.domain,
      rememberMe: formData.rememberMe,
      OtpEmail: '',
      MobileNumber: '',
      ...this.networkContext,
      UserRegisterType: UserRegisterType.Default,
    };

    return this.withPermissionsRefresh(this.store.login(payload));
  }

  // ── Multi-user profile selection ────────────────────────────────────────────
  // When a login returned more than one profile (store.profileUsers), the user picks one
  // and we re-run Generatetoken with a narrow payload for that profile: its Email +
  // PatientId + password. The password is the encrypted login-screen credential captured
  // when the profile list arrived (store.profileLoginPassword) — sent verbatim, since it is
  // already encrypted — because the list items carry no usable password of their own.
  // UserRegisterType stays consistent with the originating login (store.profileUserRegisterType)
  // so a Google/Microsoft/… multi-user flow is completed as the same provider, not Default.

  selectProfile(user: LoginUserListItem): Observable<void> {
    const payload: ProfileLoginRequest = {
      Email: user.email,
      Domain: environment.domain,
      ...this.networkContext,
      PatientId: user.PatientId ?? null,
      Password: this.store.profileLoginPassword() ?? this.encryption.encrypt(user.password),
      UserRegisterType: user.UserRegisterType ?? this.store.profileUserRegisterType(),
    };

    return this.withPermissionsRefresh(this.store.loginWithProfile(payload));
  }

  // ── Google Sign-In ────────────────────────────────────────────────────────

  loginWithGoogle(credential: string, isRegistration = false): Observable<void> {
    let jwtPayload: { email?: string; name?: string; given_name?: string; family_name?: string } = {};
    try {
      jwtPayload = JSON.parse(atob(credential.split('.')[1])) as typeof jwtPayload;
    } catch {
      // intentionally left blank — malformed token falls back to empty strings
    }

    const request: GoogleSigninRequest = {
      googleToken: credential,
      Email: jwtPayload.email ?? '',
      Name: jwtPayload.name ?? '',
      FirstName: jwtPayload.given_name ?? '',
      LastName: jwtPayload.family_name ?? '',
      ...this.networkContext,
      password: credential,
      domain: environment.domain,
      UserRegisterType: UserRegisterType.Google,
    };

    return this.withPermissionsRefresh(this.store.loginWithGoogle(request, isRegistration));
  }

  // ── LinkedIn Sign-In ──────────────────────────────────────────────────────

  loginWithLinkedIn(payload: SocialLoginPayload, isRegistration = false): Observable<void> {
    const request: LinkedinSigninRequest = {
      Email: payload.email ?? '',
      FirstName: payload.firstName ?? '',
      LastName: payload.lastName ?? '',
      Password: payload.token ?? '',
      domain: environment.domain,
      UserRegisterType: UserRegisterType.LinkedIn,
      ...this.networkContext,
    };

    return this.withPermissionsRefresh(this.store.loginWithLinkedIn(request, isRegistration));
  }

  // ── Microsoft Sign-In ─────────────────────────────────────────────────────

  loginWithMicrosoft(payload: SocialLoginPayload, isRegistration = false): Observable<void> {
    const request: MicrosoftSigninRequest = {
      MicrosoftToken: payload.token ?? '',
      Email: payload.email ?? '',
      UserName: payload.email ?? '',
      FirstName: payload.firstName ?? '',
      LastName: payload.lastName ?? '',
      password: payload.token ?? '',
      domain: environment.domain,
      UserRegisterType: UserRegisterType.Microsoft,
      ...this.networkContext,
    };

    return this.withPermissionsRefresh(this.store.loginWithMicrosoft(request, isRegistration));
  }

  // ── Facebook Sign-In ──────────────────────────────────────────────────────

  loginWithFacebook(payload: SocialLoginPayload, isRegistration = false): Observable<void> {
    const request: FacebookSigninRequest = {
      FacebookToken: payload.token ?? '',
      Email: payload.email ?? '',
      UserName: payload.email ?? '',
      FirstName: payload.firstName ?? '',
      LastName: payload.lastName ?? '',
      password: payload.token ?? '',
      domain: environment.domain,
      UserRegisterType: UserRegisterType.Facebook,
      ...this.networkContext,
    };

    return this.withPermissionsRefresh(this.store.loginWithFacebook(request, isRegistration));
  }

  // ── Apple Sign-In ─────────────────────────────────────────────────────────

  loginWithApple(payload: SocialLoginPayload, isRegistration = false): Observable<void> {
    const request: AppleSigninRequest = {
      AppleToken: payload.token ?? '',
      Email: payload.email ?? '',
      UserName: payload.email ?? '',
      FirstName: payload.firstName ?? '',
      LastName: payload.lastName ?? '',
      password: payload.token ?? '',
      domain: environment.domain,
      UserRegisterType: UserRegisterType.Apple,
      ...this.networkContext,
    };

    return this.withPermissionsRefresh(this.store.loginWithApple(request, isRegistration));
  }

  // ── Error helper ────────────────────────────────────────────────────────────
  // Single entry point every login flow (form, SDK-based, or popup/postMessage)
  // uses to surface a failure — normalizes whatever error shape it received and
  // falls back to fallbackMessage only when the error itself carries no text.

  setError(err: unknown, fallbackMessage: string): void {
    this.store.setError(resolveErrorMessage(err, fallbackMessage));
  }

  // ── Logout ─────────────────────────────────────────────────────────────────

  logout(): void {
    this.toast.clearPersistent();
    this.storage.clearAuthData();
    this.store.clearAuth();
    this.userInit.reset();
    this.masterDataStarted = false;
    // Every other feature SignalStore (Dashboard, Patients, Billing, Settings' sub-pages, …) so a
    // subsequent sign-in — possibly a different user or a different company entirely — never
    // renders data cached from this session before its own load() call resolves.
    this.storeReset.resetAll();
    this.router.navigate(['/login']);
  }

  // ── Token access ───────────────────────────────────────────────────────────

  getToken(): string | null {
    return this.storage.getToken();
  }

  /** @deprecated Use getToken() */
  getAccessToken(): string | null {
    return this.getToken();
  }

  isTokenValid(): boolean {
    const token = this.storage.getToken();
    return !!token && !isTokenExpired(token);
  }

  /** The signed-in user's active location Id, decrypted from the session snapshot. */
  getLocationId(): number | null {
    const location = this.ls.get<{ Id: string }>('Location');
    if (!location?.Id) return null;
    const id = Number(this.encryption.decrypt(location.Id));
    return Number.isFinite(id) ? id : null;
  }

  /** The signed-in user's own DoctorTypeId, decrypted from the session snapshot (see
   *  AuthStore.setAuthSuccess's `storage.setItem('DoctorTypeId', ...)`). `null` for logins that
   *  aren't doctor-type-linked (e.g. non-clinical admin accounts). */
  getDoctorTypeId(): number | null {
    const raw = this.storage.getItem('DoctorTypeId');
    if (!raw) return null;
    const id = Number(this.encryption.decrypt(raw));
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  // ── Role & Permission helpers ──────────────────────────────────────────────

  hasRole(role: string): boolean {
    return this.store.roles().includes(role);
  }

  hasAnyRole(...roles: string[]): boolean {
    return roles.some(r => this.store.roles().includes(r));
  }

  hasAllRoles(...roles: string[]): boolean {
    return roles.every(r => this.store.roles().includes(r));
  }

  hasPermission(
    moduleKey: string,
    action: 'view' | 'create' | 'edit' | 'delete' | 'export' | 'import' = 'view',
  ): boolean {
    const perm = this.store.permissions().find(p => p.moduleKey === moduleKey);
    if (!perm) return false;
    const actionMap: Record<string, keyof Permission> = {
      view: 'canView',
      create: 'canCreate',
      edit: 'canEdit',
      delete: 'canDelete',
      export: 'canExport',
      import: 'canImport',
    };
    return perm[actionMap[action]] as boolean;
  }

  canAccessMenu(menuKey: string): boolean {
    return this.store
      .subscriptionAccesses()
      .some(s => s.key === menuKey && s.IsAccess);
  }

  /** Plan-based count limit for a subscription key, or null when unlimited (no entry, no
   *  IsAccess, or limit is null/0/-1) — only a positive value is a real cap. */
  getSubscriptionLimit(menuKey: string): number | null {
    const entry = this.store.subscriptionAccesses().find(s => s.key === menuKey && s.IsAccess);
    if (!entry?.limit) return null;
    const parsed = Number(entry.limit);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  /** True only when the key has a positive plan limit and currentCount has reached it — used to
   *  block just the Add/Create action while leaving the rest of an already-accessible module
   *  (existing items, edit/delete, etc.) untouched. Callers must still gate on canAccessMenu()
   *  separately for the IsAccess = false case. */
  isAddLimitReached(menuKey: string, currentCount: number): boolean {
    const limit = this.getSubscriptionLimit(menuKey);
    return limit !== null && currentCount >= limit;
  }

  canShowMenuItem(moduleKey: string | null | undefined): boolean {
    if (!moduleKey) return true;
    if (!this.store.permissionsLoaded()) return true;
    return this.hasPermission(moduleKey, 'view');
  }

  /** Same fail-open visibility gate as canShowMenuItem, but matched on the backend's numeric
   *  Menu enum value (Permission.id, set from dto.MenuEnum) instead of the MenuName string.
   *  Several distinct Menu entries share the same Description/MenuName across different parents
   *  (e.g. "Active Patients" is both Menu.PatientCountDetails=113 under Dashboard and
   *  Menu.PatientActiveCount=138 under Patient) — a string match can't tell them apart, so any
   *  widget/KPI gate that needs to target one specific entry should use this instead. */
  canShowMenuItemByMenuId(menuId: number | null | undefined): boolean {
    if (menuId == null) return true;
    if (!this.store.permissionsLoaded()) return true;
    const perm = this.store.permissions().find(p => p.id === String(menuId));
    return !!perm?.canView;
  }

  /** Same fail-open action gate as canPerformAction, but matched on the backend's numeric Menu
   *  enum value instead of the MenuName string — the numeric-id counterpart of
   *  canShowMenuItemByMenuId, for a standalone permission entry (e.g. Menu.GeneratePaymentReceipt)
   *  whose Add/Edit/Delete/Export flags gate something other than the page's own module. */
  canPerformActionByMenuId(
    menuId: number | null | undefined,
    action: 'create' | 'edit' | 'delete' | 'export' | 'import',
  ): boolean {
    if (menuId == null) return true;
    if (!this.store.permissionsLoaded()) return true;
    const perm = this.store.permissions().find(p => p.id === String(menuId));
    if (!perm) return false;
    const actionMap: Record<string, keyof Permission> = {
      create: 'canCreate',
      edit: 'canEdit',
      delete: 'canDelete',
      export: 'canExport',
      import: 'canImport',
    };
    return perm[actionMap[action]] as boolean;
  }

  /** Same grace-period fail-open as canShowMenuItem, for gating Add/Edit/Delete/Export action
   *  buttons instead of menu visibility — shows the action until permissions have actually
   *  resolved for this session, then hides it once we know it's denied. */
  canPerformAction(
    moduleKey: string | null | undefined,
    action: 'create' | 'edit' | 'delete' | 'export' | 'import',
  ): boolean {
    if (!moduleKey) return true;
    if (!this.store.permissionsLoaded()) return true;
    return this.hasPermission(moduleKey, action);
  }

  // ── Trial / demo access ────────────────────────────────────────────────────

  loginAsTrial(): void {
    this.store.setAuthSuccess({
      isLoggedIn: true,
      isTrial: true,
      accessToken: null,
      userId: 'trial-user',
      userName: 'Demo User',
      email: 'demo@trial.com',
      companyId: null,
      companyName: null,
      roles: ['Admin'],
      permissions: [],
      permissionsLoaded: false,
      permissionsLoadOffline: false,
      subscriptionAccesses: [],
      redirectMenu: '/dashboard',
      error: null,
      pendingSignupEmail: null,
      pendingSignupPhone: null,
      pendingSignupId: null,
      pendingSignupRequest: null,
      needsPlanSelection: false,
      needsProfileCompletion: false,
      selectedPlanId: null,
      onboardingProvisioning: false,
      onboardingJobDone: false,
      needsPayment: false,
      paymentCompanyId: null,
      paymentResponseData: null,
      needsTermsAcceptance: false,
      termsSkipCount: 0,
      resetPasswordEmail: null,
      resetPasswordOtpVerified: false,
    });
    this.router.navigate(['/dashboard']);
  }

  // ── OTP login ──────────────────────────────────────────────────────────────

  sendOtp(email: string): Observable<void> {
    const payload: SendOtpRequest = { Email: email, Domain: environment.domain };
    return this.store.sendOtp(payload);
  }

  verifyOtp(email: string, code: string): Observable<void> {
    const payload: VerifyOtpRequest = {
      Email: email,
      Code: this.encryption.encrypt(code),
      Domain: environment.domain,
      ipAddress: this.ipAddress,
      ipLocation: this.ipLocation,
      deviceType: this.deviceType,
      IsTwoFactorAuthenticated: true,
      PatientId: null,
    };

    return this.withPermissionsRefresh(this.store.verifyOtp(payload));
  }

  // ── Signup ───────────────────────────────────────────────────────────────
  // Creates the account and sends the email-verification OTP via
  // api/User/SignUp.

  signup(formData: SignupFormData): Observable<void> {
    const payload: SignupRequest = {
      Email: formData.email,
      MobileNumber: formData.phoneNumber,
      CountryCode: formData.phoneCountryCode,
      Password: this.encryption.encrypt(formData.password),
      IPAddress: this.ipAddress,
      IPLocation: this.ipLocation,
    };

    return this.store.startSignup(payload, formData.phoneNumber);
  }

  verifySignupOtp(code: string): Observable<void> {
    return this.withPermissionsRefresh(this.store.verifySignupOtp(code));
  }

  selectSignupPlan(selection: PlanSelection): void {
    this.store.selectPlan(selection);
  }

  backToPlanSelection(): void {
    this.store.backToPlanSelection();
  }

  /**
   * Live availability check while typing the clinic name — taken when either a non-null Data
   * is returned, or the API reports the CLINIC_NAME_CONFLICT_RESPONSE_CODE envelope failure.
   */
  checkCompanyCodeExists(companyCode: string, locationId: number = SIGNUP_LOCATION_ID): Observable<boolean> {
    // Send the plain/original company id (as decoded from the JWT) — never the encrypted
    // copy persisted to localStorage — so the backend can exclude the caller's own record.
    const companyId = Number(this.store.companyId()) || 0;

    return this.api
      .post<CheckCompanyCodeExistsResponse>(API_ENDPOINTS.auth.checkCompanyCodeExists, null, {
        params: { CompanyCode: companyCode, locationId, CompanyId: companyId },
        // Fires on every debounced keystroke while typing the clinic name — a global spinner
        // flashing per key press would be more distracting than helpful; checkingClinicName()
        // already shows an inline "checking…" indicator in complete-profile-modal.component.ts.
        skipLoader: true,
      })
      .pipe(
        map(response => {
          if (response?.ResponseCode === CLINIC_NAME_CONFLICT_RESPONSE_CODE) return true;
          return !!response?.Success && response.Data !== null && response.Data !== undefined;
        }),
        catchError(() => of(false)),
      );
  }

  /**
   * Live availability check while typing the signup email — resolves true when
   * the email is already registered. Fails safe (resolves false) on error so a
   * transient network issue never blocks the form; the backend re-validates on submit.
   *
   * Runs on the pre-auth signup screen with no token, so `skipAuthRedirect` keeps a 401 from
   * being treated as an expired session and navigating to /login mid-typing.
   *
   * A taken email comes back as the EmailExists envelope failure (Success:false,
   * ResponseCode 404), not as Data:true — see CommonController.CheckSignupUserEmailExists.
   */
  checkEmailExists(email: string): Observable<boolean> {
    const request: CheckSignupUserEmailExistsRequest = { Email: email };

    return this.api
      .post<CheckSignupUserEmailExistsResponse>(API_ENDPOINTS.auth.checkSignupUserEmailExists, request, {
        skipAuthRedirect: true,
        skipLoader: true,
      })
      .pipe(
        map(response => {
          if (response?.ResponseCode === ResponseCode.EmailExists) return true;
          return !!response?.Success && response.Data === true;
        }),
        catchError(() => of(false)),
      );
  }

  completeSignupProfile(formData: CompleteProfileFormData, locationId: number = SIGNUP_LOCATION_ID): void {
    const country = this.store.countries().find(c => c.CountryName === formData.country) ?? null;
    const planIds = this.subscriptionPlan.getIdsFor(this.store.selectedPlanId() ?? 'basic');

    const request: UpdateOnboardingDetailsRequest = {
      Id: Number(this.store.companyId()) || 0,
      Salutation: formData.salutation,
      FirstName: formData.firstName,
      LastName: formData.lastName,
      MobileNumber: formData.phoneNumber,
      PhoneNumber: formData.phoneNumber,
      Gender: null,
      Email: this.store.email() ?? '',
      CompanyCode: formData.clinicName,
      CompanyName: formData.clinicName,
      CompanyAddress: formData.clinicAddress,
      Domain: '',
      IsActive: false,
      IsTermsAndCondition: true,
      IPAddress: this.ipAddress,
      ConnectionString: '',
      ConnectionStringEF: '',
      CurrencyId: country?.Id ?? 0,
      CompanyDateFormat: null,
      CompanyDecimalFormat: null,
      Password: '',
      CurrencyObj: { Id: 0, IsDeleted: true, CurrencyCode: '', CurrencySymbol: '' },
      SubscriptionPlanId: planIds.subscriptionPlanId,
      PlanPrice: this.store.selectedPlanPrice() ?? 0,
      IsMonth: this.store.selectedBillingCycle() !== 'annual',
      City: null,
      State: null,
      CouponId: null,
      CouponDiscount: null,
      PlanId: planIds.planId,
      TransactionCurrencyId: country?.Id ?? 0,
      CreditValue: 0,
      IsDoctor: false,
    };

    this.store.completeProfile(request, locationId);
  }

  clearCompleteProfileError(): void {
    this.store.clearCompleteProfileError();
  }

  loadCountries(locationId: number = SIGNUP_LOCATION_ID): void {
    this.store.loadCountries(locationId);
  }

  // ── Post-onboarding Terms & Conditions ──────────────────────────────────

  skipTermsAcceptance(locationId: number = SIGNUP_LOCATION_ID): void {
    this.store.skipTerms(locationId);
  }

  acceptTermsAcceptance(): void {
    this.store.acceptTerms();
  }

  // ── "Plan expiring soon" popup ────────────────────────────────────────────

  markUpgradePopupShown(): void {
    this.store.markUpgradePopupShown();
  }

  // ── Paid-plan Razorpay payment (between provisioning and login) ──────────
  // Shell reads needsPayment(), drives the Razorpay checkout via
  // OnboardingPaymentService using getPaymentContext(), then finalizes.

  /** Location id used throughout signup onboarding (no real location exists yet). */
  readonly signupLocationId = SIGNUP_LOCATION_ID;

  /** Normalised plan/company context for the Razorpay payloads and checkout prefill. */
  getPaymentContext(companyId: number): PaymentPlanContext {
    const planId = this.store.selectedPlanId() ?? 'standard';
    const ids = this.subscriptionPlan.getIdsFor(planId);
    return resolvePlanContext({
      planId,
      companyId,
      userId: this.store.userId(),
      currency: this.store.selectedCurrencyCode(),
      isYearly: this.store.selectedBillingCycle() === 'annual',
      planPrice: this.store.selectedPlanPrice(),
      companyName: this.store.companyName(),
      email: this.store.email(),
      mobileNumber: this.store.pendingSignupPhone(),
      subscriptionPlanId: ids.subscriptionPlanId,
      planTypeId: ids.planId,
      // GetJobStatus carries no plan/company ids (see JobStatusData) — resolvePlanContext
      // sources them from the session values passed above.
    });
  }

  /** After the Razorpay checkout completes, run LoginForAdmin → Terms. */
  finalizeOnboardingAfterPayment(companyId: number): void {
    this.store.finalizeOnboardingAfterPayment(companyId, SIGNUP_LOCATION_ID);
  }

  /** Payment dismissed/failed — keep onboarding recoverable, optionally show a message. */
  cancelPayment(message: string | null = null): void {
    this.store.cancelPayment(message);
  }

  // ── Forgot / Reset Password ─────────────────────────────────────────────────
  // Three-step flow: send a verification code to the email, verify that code,
  // then submit a new password — each step delegates the HTTP call + state to AuthStore.

  sendForgotPasswordOtp(email: string): Observable<void> {
    const payload: ForgotPasswordRequest = { Email: email, Domain: environment.domain };
    return this.store.sendForgotPasswordOtp(payload);
  }

  verifyForgotPasswordOtp(code: string): Observable<void> {
    const payload: VerifyForgotPasswordOtpRequest = {
      Email: this.store.resetPasswordEmail() ?? '',
      Domain: environment.domain,
      code: this.encryption.encrypt(code),
      isTwoFactorAuth: false,
    };
    return this.store.verifyForgotPasswordOtp(payload);
  }

  resetPassword(newPassword: string, confirmPassword: string): Observable<void> {
    const email = this.store.resetPasswordEmail();
    if (!email) {
      return throwError(() => new Error('Your session has expired. Please start again.'));
    }

    const payload: ResetPasswordRequest = {
      Email: email,
      NewPassword: this.encryption.encrypt(newPassword),
      ConfirmPassword: this.encryption.encrypt(confirmPassword),
      Domain: environment.domain,
    };
    return this.store.resetPassword(payload);
  }

  clearForgotPasswordState(): void {
    this.store.clearForgotPasswordState();
  }

  // Reset via the emailed AccessToken link (e.g. from the users-permissions "Reset Password"
  // action) — no prior OTP-verify step, the token itself proves ownership.
  resetPasswordWithToken(accessToken: string, newPassword: string, confirmPassword: string): Observable<void> {
    const payload: ResetPasswordWithTokenRequest = {
      AccessToken: accessToken,
      NewPassword: this.encryption.encrypt(newPassword),
      ConfirmPassword: this.encryption.encrypt(confirmPassword),
    };
    return this.store.resetPasswordWithToken(payload);
  }
  
  forgotPassword(): Observable<boolean> {
    return of(true);
  }

  // ── Device & network info ──────────────────────────────────────────────────

  private getUserInfo(): void {
    this.deviceType = this.getLoginDeviceType();

    const cachedIp = this.ls.getString('IA');
    const cachedLocation = this.ls.getString('ipLocation');

    if (cachedIp) this.ipAddress = cachedIp;
    if (cachedLocation) this.ipLocation = cachedLocation;

    if (!cachedIp || !cachedLocation) {
      this.api.get<{ ip: string; city: string }>(API_ENDPOINTS.external.ipInfo).subscribe({
        next: data => {
          if (!cachedIp && data?.ip) {
            this.ipAddress = data.ip;
            this.ls.set('IA', this.ipAddress);
          }
          if (!cachedLocation && data?.city) {
            this.ipLocation = data.city;
            this.ls.set('ipLocation', this.ipLocation);
          }
        },
        error: () => {
          // intentionally left blank — IP lookup is best-effort
        },
      });
    }
  }

  private getLoginDeviceType(): string {
    const isPwa =
      window.matchMedia?.('(display-mode: standalone)').matches ||
      (window.navigator as { standalone?: boolean }).standalone === true;
    if (isPwa) return 'PWA';

    const ua = navigator.userAgent;
    const isTablet =
      /tablet|ipad|playbook|silk/i.test(ua) ||
      (/android/i.test(ua) && !/mobile/i.test(ua));
    if (isTablet) return 'Tablet';

    const isMobile =
      /mobile|android|iphone|ipod|blackberry|opera mini|iemobile|wpdesktop/i.test(ua);
    if (isMobile) return 'Mobile';

    return 'Web';
  }

  // ── Private helpers ────────────────────────────────────────────────────────

  // ── Admin-panel impersonation login ───────────────────────────────────────
  // URL format: /#/Dashboard?<encodedJWT>&<encodedUserId>&<routingPage>
  // handleAdminLoginFromUrl() stores the JWT and sets pendingAdminUserId (sync).
  // validateAdminLoginAsync() calls AdminLoginById, writes location/identity data,
  // then calls loadMasterData() with the correct locationId in its finally block.

  // userId from the admin-login URL; null for normal logins.
  private pendingAdminUserId: string | null = null;

  // Sync phase: validates and stores the JWT before restoreSession() reads it.
  private handleAdminLoginFromUrl(): void {
    const hash = window.location.hash; // e.g. "#/Dashboard?ENCODED_JWT&ENCODED_USERID&Dashboard"
    const questionIdx = hash.indexOf('?');
    if (questionIdx === -1) return;

    const queryString = hash.slice(questionIdx + 1);
    const params = queryString.split('&').map(p => {
      try { return decodeURIComponent(p); } catch { return p; }
    });

    if (params.length < 2 || !params[0] || !params[1]) return;

    const token = params[0];
    const userId = params[1];

    if (token === environment.googleloginkey) return;
    // JWT must have exactly 3 parts and be decodable — prevents non-JWT strings from touching storage.
    if ((token.match(/\./g) ?? []).length !== 2) return;
    if (!decodeJwt(token)) return;

    this.storage.setToken(token);
    this.storage.setUserId(userId); // required: UserInitApiService.getUserId() reads pp_user_id directly.
    this.storage.setRememberMe(true); // prevents initCrossTabSync from evicting this new tab's session.
    this.storage.clearOnboardingIncomplete();
    this.storage.setItem('AdminLogin', true);

    this.pendingAdminUserId = userId;

    // Clean URL so a reload does not re-trigger this path.
    const cleanHash = hash.slice(0, questionIdx).toLowerCase();
    window.history.replaceState(null, '', window.location.pathname + cleanHash);
  }

  // Async phase: calls AdminLoginById for location/identity/subscription data, then loadMasterData().
  private async validateAdminLoginAsync(): Promise<void> {
    const userId = this.pendingAdminUserId;
    if (!userId) return;

    try {
      interface AdminLoginByIdData {
        // Identity / display
        Id: string;
        Name: string | null;
        CompanyId: number;
        CompanyName: string | null;
        AspNetRoleList: string[];
        AspNetRoleString: string | null;
        // Location
        LocationId: number | null;
        LocationName: string | null;
        // Plan / subscription
        SelectedPlanName: string | null;
        SubscriptionAccesses: SubscriptionAccess[];
      }

      const response = await firstValueFrom(
        this.api.post<{ Success: boolean; Data: AdminLoginByIdData }>(
          API_ENDPOINTS.auth.adminLoginById,
          null,
          {
            params: { userId, secretKey: environment.adminloginkey },
            skipLocationId: true,   // no location in storage yet for this fresh tab.
            skipAuthRedirect: true, // best-effort call; JWT is the real auth.
          },
        ).pipe(timeout(10_000)), // prevent a hung server from blocking loadMasterData() forever.
      );

      if (response?.Success) {
        const data = response.Data;

        // Write location before loadMasterData() so GetUserAccessDetails uses the correct locationId.
        if (data.LocationId) {
          this.ls.set('Location', {
            Id: this.encryption.encrypt(String(data.LocationId)),
            Name: data.LocationName ?? '',
            TimeZone: null,
            LocationIds: [data.LocationId],
          });
          this.locationStore.syncFromStorage();
        }

        if (data.Name) this.storage.setItem('Name', data.Name);
        if (data.AspNetRoleList?.length) this.storage.setRoles(data.AspNetRoleList);
        if (data.CompanyName) this.storage.setItem('CompanyName', data.CompanyName);

        if (data.SubscriptionAccesses?.length) {
          this.store.updateSubscriptions(data.SubscriptionAccesses);
          const featureMap: Record<string, boolean> = {};
          data.SubscriptionAccesses.forEach(s => { featureMap[s.key] = s.IsAccess; });
          this.storage.setItem('FeatureAccess', featureMap);
        }

        if (data.SelectedPlanName) {
          this.store.setPlanName(data.SelectedPlanName);
          this.storage.setItem('SelectedPlanName', data.SelectedPlanName);
        }
      }
    } catch {
      // Swallow errors — JWT session is already established; loadMasterData() fires in finally.
    } finally {
      // Always fire loadMasterData() here with the correct locationId now in localStorage.
      this.pendingAdminUserId = null;
      const storeUserId = this.store.userId();
      if (storeUserId) this.loadMasterData(storeUserId);
    }
  }

  private restoreSession(): void {
    const token = this.storage.getToken();
    if (!token || isTokenExpired(token)) {
      if (token) this.storage.clearAuthData();
      return;
    }

    // A signup/social-registration account can still have a choose-plan /
    // complete-profile / terms-acceptance gate pending even though a valid token
    // already exists (see auth.store.ts persistLoginData/applySocialOutcome). If
    // the tab was refreshed or reopened before that gate was passed, resuming the
    // session here would drop the user straight onto the dashboard, silently
    // skipping the step — so treat it as no session at all and force a fresh
    // /login instead.
    if (this.storage.isOnboardingIncomplete()) {
      this.storage.clearAuthData();
      return;
    }

    const jwt = decodeJwt(token);
    if (!jwt) {
      this.storage.clearAuthData();
      return;
    }

    const userId = jwt.UserId ?? this.storage.getUserId() ?? null;

    this.store.setAuthSuccess({
      isLoggedIn: true,
      isTrial: false,
      accessToken: token,
      userId,
      userName: this.storage.getItem('Name'),
      email: jwt.email ?? null,
      companyId: jwt.CompanyId ?? null,
      companyName: null,
      roles: this.storage.getRoles(),
      permissions: [],
      permissionsLoaded: false,
      permissionsLoadOffline: false,
      subscriptionAccesses: [],
      redirectMenu: '/dashboard',
      error: null,
      pendingSignupEmail: null,
      pendingSignupPhone: null,
      pendingSignupId: null,
      pendingSignupRequest: null,
      needsPlanSelection: false,
      needsProfileCompletion: false,
      selectedPlanId: null,
      onboardingProvisioning: false,
      onboardingJobDone: false,
      needsPayment: false,
      paymentCompanyId: null,
      paymentResponseData: null,
      needsTermsAcceptance: false,
      termsSkipCount: 0,
      resetPasswordEmail: null,
      resetPasswordOtpVerified: false,
    });
    this.store.setPlanName(this.storage.getItem('SelectedPlanName'));

    // isOnboardingIncomplete() above already forces a fresh /login instead of resuming a session
    // still mid-onboarding, so onboardingBlocking() is always false by this point — no gate needed.
    // Admin redirect: skip — validateAdminLoginAsync() finally block owns loadMasterData().
    if (this.pendingAdminUserId) return;
    if (userId) this.loadMasterData(userId);
  }

  // ── Cross-tab session sync ─────────────────────────────────────────────────
  // The auth token always lives in localStorage, so every open tab observes the
  // same session. Login/logout in one tab must therefore propagate live to every
  // other tab, and a session that was never "remembered" must not survive a full
  // browser close+reopen even though localStorage itself would otherwise keep it.
  //
  // "Is any sibling tab still open" is answered by reading a shared tab registry
  // in localStorage rather than a live ping/pong: every open tab refreshes its own
  // timestamp on a heartbeat, so a newly opened tab can decide synchronously,
  // with no race against a fixed round-trip timeout.
  //
  // `isNewTab` (sessionStorage TAB_ID_KEY missing) used to be treated as proof this
  // is a fresh browsing context, on the assumption that sessionStorage always
  // survives a reload. That assumption does not hold in every real browser/profile
  // (observed: sessionStorage empty on an ordinary F5 in a normal, non-incognito
  // tab), which was wiping valid sessions on a plain refresh. So `isNewTab` alone no
  // longer gates the cleanup below — `hasAnotherActiveTab()` is the deciding signal
  // instead: `onPageHide` (below) no longer deletes this tab's registry entry on
  // unload, so a same-tab reload still finds its own pre-refresh heartbeat entry
  // (under the old, now-foreign tabId) fresh in the registry and is correctly read
  // as "another" live entry — surviving even when sessionStorage does not. Only a
  // tab whose entry has gone fully stale (TAB_STALE_THRESHOLD_MS with no heartbeat
  // — i.e. actually closed, not just reloaded) is absent from the registry.

  private resolveTabId(): string {
    let id = sessionStorage.getItem(TAB_ID_KEY);
    if (!id) {
      id = this.generateTabId();
      sessionStorage.setItem(TAB_ID_KEY, id);
      this.isNewTab = true;
    }
    return id;
  }

  private generateTabId(): string {
    if (typeof crypto === 'undefined') return `tab-${Date.now()}`; // NOSONAR - no crypto API available; tab id is not security-sensitive
    if (crypto.randomUUID) return crypto.randomUUID();
    // Math.random() is not cryptographically secure; use crypto.getRandomValues instead so
    // this tab identifier (non-security-sensitive) doesn't trip Sonar's insecure-PRNG rule.
    const bytes = new Uint32Array(2);
    crypto.getRandomValues(bytes);
    return `tab-${Date.now()}-${bytes[0].toString(36)}${bytes[1].toString(36)}`;
  }

  private initCrossTabSync(): void {
    window.addEventListener('storage', this.onStorageEvent);
    window.addEventListener('pagehide', this.onPageHide);

    if (
      this.isNewTab &&
      !this.storage.isPersistent() &&
      this.storage.getToken() &&
      !this.hasAnotherActiveTab()
    ) {
      this.toast.clearPersistent();
      this.storage.clearAuthData();
      this.store.clearAuth();
      this.userInit.reset();
      this.masterDataStarted = false;
      this.storeReset.resetAll();
      this.router.navigate(['/login']);
    }

    this.registerActiveTab();
    this.heartbeatTimer = setInterval(() => this.registerActiveTab(), TAB_HEARTBEAT_INTERVAL_MS);
  }

  private readonly onStorageEvent = (event: StorageEvent): void => {
    if (event.key !== AUTH_TOKEN_STORAGE_KEY) return;

    if (event.newValue && !this.store.isLoggedIn()) {
      this.restoreSession();
      if (this.store.isLoggedIn()) {
        this.router.navigateByUrl(this.redirectMenu() || '/dashboard');
      }
    } else if (!event.newValue && this.store.isLoggedIn() && !this.store.isTrial()) {
      this.toast.clearPersistent();
      this.store.clearAuth();
      this.userInit.reset();
      this.masterDataStarted = false;
      this.storeReset.resetAll();
      this.router.navigate(['/login']);
    }
  };

  // Deliberately does NOT remove this tab's registry entry — `pagehide` fires on an
  // ordinary reload just as much as on a real close, and a same-tab reload must still
  // see its own pre-refresh heartbeat as evidence a session was already live (see the
  // cross-tab sync comment above `resolveTabId`). A genuinely closed tab's entry is
  // instead left to age out naturally once `TAB_STALE_THRESHOLD_MS` passes with no
  // further heartbeat.
  private readonly onPageHide = (): void => {
    if (this.heartbeatTimer !== null) clearInterval(this.heartbeatTimer);
  };

  private hasAnotherActiveTab(): boolean {
    const registry = this.readTabRegistry();
    const now = Date.now();
    return Object.entries(registry).some(
      ([id, lastSeen]) => id !== this.tabId && now - lastSeen < TAB_STALE_THRESHOLD_MS,
    );
  }

  private registerActiveTab(): void {
    const registry = this.readTabRegistry();
    registry[this.tabId] = Date.now();
    this.writeTabRegistry(this.pruneStaleTabs(registry));
  }

  private pruneStaleTabs(registry: TabRegistry): TabRegistry {
    const now = Date.now();
    const pruned: TabRegistry = {};
    Object.entries(registry).forEach(([id, lastSeen]) => {
      if (now - lastSeen < TAB_STALE_THRESHOLD_MS) pruned[id] = lastSeen;
    });
    return pruned;
  }

  private readTabRegistry(): TabRegistry {
    try {
      return JSON.parse(localStorage.getItem(ACTIVE_TABS_KEY) ?? '{}') as TabRegistry;
    } catch {
      return {};
    }
  }

  private writeTabRegistry(registry: TabRegistry): void {
    localStorage.setItem(ACTIVE_TABS_KEY, JSON.stringify(registry));
  }

  private buildInitials(name: string | null): string {
    if (!name) return '?';
    return name
      .split(/\s+/)
      .slice(0, 2)
      .map(w => w[0]?.toUpperCase() ?? '')
      .join('');
  }
}
